import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const AI_LIMIT = 20;

export default function AIPanel({ context }) {
  const { profile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [queryCount, setQueryCount] = useState(0);
  const msgsRef = useRef(null);
  const [isListening, setIsListening] = useState(false);

  useEffect(() => {
    if (msgsRef.current) {
      msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (profile && context?.opportunities) {
      generateLoginBrief();
    }
  // eslint-disable-next-line
  }, [profile?.id]);

  function buildSystemPrompt() {
    const { leads = [], opportunities = [], clients = [], activities = [] } = context || {};
    const today = new Date();

    const dealCtx = opportunities
      .filter(o => o.stage !== 'Closed Won' && o.stage !== 'Closed Lost' && !o.archived)
      .map(o => {
        const lastAct = activities.find(a => a.linked_opp_id === o.id);
        const days = lastAct ? Math.round((today - new Date(lastAct.date)) / 86400000) : null;
        return `${o.company} | ${o.stage} | $${o.value?.toLocaleString()} | Next: ${o.next_step || 'NONE'}${days !== null ? ` | ${days}d since last activity` : ' | No activity logged'}`;
      }).join('\n');

    const stalledDeals = opportunities
      .filter(o => {
        if (o.stage === 'Closed Won' || o.stage === 'Closed Lost' || o.archived || o.next_step) return false;
        const last = activities.find(a => a.linked_opp_id === o.id);
        const days = last ? Math.round((today - new Date(last.date)) / 86400000) : 999;
        return days >= 7;
      })
      .map(o => `${o.company} ($${Math.round((o.value || 0) / 1000)}K)`)
      .join(', ');

    const clientCtx = clients
      .filter(c => c.stage === 'Current' && !c.archived)
      .map(c => `${c.name} ($${c.contract_value?.toLocaleString()}) | Next: ${c.next_step || 'NONE'}`)
      .join('; ');

    const recentActs = activities.slice(0, 8)
      .map(a => `${a.subject || a.type} - ${a.outcome} (${a.date})`)
      .join('; ');

    return `You are the doer-seller AI coach, powered by Stimulyst. You coach B2B consultants to win large corporate clients.

CONSULTANT: ${profile?.name || 'the consultant'}, ${profile?.firm_name || ''}

ACTIVE DEALS:
${dealCtx || 'No active deals'}

CURRENT CLIENTS: ${clientCtx || 'None'}

RECENT ACTIVITIES: ${recentActs || 'None logged'}
${stalledDeals ? `\nSTALLED DEALS (7+ days, no next step): ${stalledDeals}` : ''}

ROLE: Be a direct, proactive revenue coach. When drafting messages (email, SMS, LinkedIn), write them fully — specific to the deal and contact. Reference actual names, values, and context. Never be generic.`;
  }

  async function generateLoginBrief() {
    const { opportunities = [] } = context || {};
    const active = opportunities.filter(o => o.stage !== 'Closed Won' && o.stage !== 'Closed Lost' && !o.archived);
    const totalValue = active.reduce((s, o) => s + (o.value || 0), 0);
    const noNextStep = active.filter(o => !o.next_step);

    const userMsg = `Give me a sharp 3-point revenue brief for today. Format as:
1. [Most urgent action — name the company and dollar value]
2. [Biggest pipeline risk right now]  
3. [The one IPA I should do today to move the most money]

Pipeline: ${active.length} deals, $${Math.round(totalValue / 1000)}K total.${noNextStep.length ? ` ${noNextStep.length} deals have no next step (${noNextStep.map(o => o.company).join(', ')}).` : ''}`;

    setMessages([{
      role: 'ai',
      text: '<em style="color:var(--gray3);font-size:12px">Generating your daily brief...</em>'
    }]);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 400,
          system: buildSystemPrompt(),
          messages: [{ role: 'user', content: userMsg }]
        })
      });
      const data = await resp.json();
      const text = (data.content || []).filter(b => b.type === 'text').map(b => b.text).join('');
      setMessages([{
        role: 'ai',
        text: `<div style="font-size:10px;font-weight:700;letter-spacing:0.09em;text-transform:uppercase;color:var(--teal);margin-bottom:8px">Today's Revenue Brief</div>${text.replace(/\n/g, '<br/>')}`,
        isHtml: true
      }]);
      setQueryCount(q => q + 1);
    } catch {
      setMessages([{
        role: 'ai',
        text: `${profile?.name?.split(' ')[0] || 'Hey'}, your pipeline is loaded. Ask me anything about your deals.`
      }]);
    }
  }

  function startVoice() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert('Voice input works in Chrome and Edge only.');
      return;
    }
    const rec = new SR();
    rec.lang = 'en-US';
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    setIsListening(true);
    rec.start();
    rec.onresult = e => {
      const transcript = e.results[0][0].transcript;
      setInput(transcript);
      setIsListening(false);
    };
    rec.onerror = () => setIsListening(false);
    rec.onend = () => setIsListening(false);
  }

  async function sendMessage() {
    const msg = input.trim();
    if (!msg || loading || queryCount >= AI_LIMIT) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setLoading(true);

    try {
      const resp = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 600,
          system: buildSystemPrompt(),
          messages: [{ role: 'user', content: msg }]
        })
      });
      const data = await resp.json();
      const text = (data.content || []).filter(b => b.type === 'text').map(b => b.text).join('');
      setMessages(prev => [...prev, { role: 'ai', text: text.replace(/\n/g, '<br/>'), isHtml: true }]);
      setQueryCount(q => q + 1);
    } catch {
      setMessages(prev => [...prev, { role: 'ai', text: 'Something went wrong. Try again.' }]);
    }
    setLoading(false);
  }

  const quickActions = [
    { label: 'Draft Email', prompt: 'Draft a follow-up email for my highest-value active deal. Make it specific, warm, and include a clear next step.' },
    { label: 'Draft SMS', prompt: 'Draft a short follow-up text for my top priority deal. Under 160 characters. Casual but professional.' },
    { label: 'Draft LinkedIn', prompt: 'Draft a LinkedIn follow-up message for my top priority deal. Personalized, concise, not salesy.' },
    { label: 'Prep for Call', prompt: 'Prep me for my next call with my highest-priority deal. Give me: the goal, 3 questions to ask, likely objections, and what success looks like.' },
  ];

  return (
    <div className="ai-panel">
      <div className="ai-panel-hdr">
        <div className="ai-panel-title">AI Revenue Coach</div>
        <div className="ai-panel-sub">Trained on your pipeline + Stimulyst methodology</div>
        <div className="ai-usage">{AI_LIMIT - queryCount} / {AI_LIMIT} queries remaining today</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 8 }}>
          {quickActions.map(qa => (
            <button
              key={qa.label}
              className="btn btn-ghost btn-sm"
              style={{ fontSize: 10, padding: '3px 8px', color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.2)' }}
              onClick={() => { setInput(qa.prompt); }}
            >
              {qa.label}
            </button>
          ))}
        </div>
      </div>

      <div className="ai-msgs" ref={msgsRef}>
        {messages.map((m, i) => (
          <div key={i} className={`ai-msg ai-msg-${m.role}`}>
            {m.role === 'ai' && <div className="ai-label">AI Coach</div>}
            <div
              className="ai-bubble"
              dangerouslySetInnerHTML={m.isHtml ? { __html: m.text } : undefined}
            >
              {!m.isHtml && m.text}
            </div>
            {m.role === 'ai' && (
              <div style={{ display: 'flex', gap: 6, marginTop: 5 }}>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: 10, padding: '2px 8px', opacity: 0.6 }}
                  onClick={() => {
                    const text = m.isHtml ? m.text.replace(/<[^>]+>/g, '') : m.text;
                    navigator.clipboard.writeText(text);
                  }}
                >
                  Copy
                </button>
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="ai-msg ai-msg-ai">
            <div className="ai-label">AI Coach</div>
            <div className="ai-bubble" style={{ color: 'var(--gray3)', fontStyle: 'italic' }}>Thinking...</div>
          </div>
        )}
      </div>

      <div className="ai-input-wrap">
        <textarea
          className="ai-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Ask about your pipeline..."
          rows={1}
        />
        <button
          className="ai-send"
          onClick={startVoice}
          disabled={isListening}
          title="Voice input (Chrome/Edge)"
          style={{
            background: isListening ? 'var(--red)' : 'var(--gray1)',
            color: isListening ? '#fff' : 'var(--navy)',
            marginRight: 4,
          }}
        >
          {isListening ? '🔴' : '🎤'}
        </button>
        <button className="ai-send" onClick={sendMessage} disabled={loading}>▶</button>
      </div>
    </div>
  );
}
